import type PersonaType from 'owa-service/lib/contract/PersonaType';
import type EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import type PhoneNumberAttributedValue from 'owa-service/lib/contract/PhoneNumberAttributedValue';
import type PostalAddressAttributedValue from 'owa-service/lib/contract/PostalAddressAttributedValue';
import type StringAttributedValue from 'owa-service/lib/contract/StringAttributedValue';

/*
 * Type definitions from midgard:/packages/lpc-core/src/Models/LivePersonaCardModelsReka.ts
 */

export function owsPersonaToLpcPerson(owsPersona: PersonaType): any {
    return {
        kind: 'Person',
        isExplicitContact: true,
        olsPersonaId: owsPersona.PersonaId,
        aadObjectId: owsPersona.ADObjectId,
        displayName: owsPersona.DisplayName,
        firstName: owsPersona.GivenName,
        lastName: owsPersona.Surname,
        personaCoinName: lpcPersonaCoinName(owsPersona),
        email: lpcEmailAddress(owsPersona.EmailAddress),
        extraEmails: lpcExtraEmailAddresses(owsPersona.EmailAddresses, owsPersona.EmailAddress),
        jobTitle: owsPersona.Title,
        department: owsPersona.Department,
        officeLocation: owsPersona.Location,
        city: owsPersona.WorkCity,
        imAddresses: lpcImAddresses(owsPersona),
        phoneNumbersAndUrls: lpcPhoneNumbers(owsPersona),
        company: owsPersona.CompanyName,
        postalAddresses: lpcPostalAddresses(owsPersona),
        birthday: lpcBirthDay(owsPersona),
        anniversary: lpcAnniversary(owsPersona),
        yomiCompany: owsPersona.YomiCompanyName,
        websites: lpcWebsites(owsPersona),
        nickName: owsPersona.Nickname,
        yomiName: lpcYomiName(owsPersona),
        personalNotes: [],
        assistantName: lpcAssistantName(owsPersona),
        managerFromContacts: lpcManagerFromContacts(owsPersona),
        significantOtherName: lpcSignificantOtherName(owsPersona),
    };
}

type LpcEmailData = { Address: string; Kind: LpcEmailType };

type LpcEmailType = 'Undefined' | 'Home' | 'Business' | 'Other';

type ImData = {
    ImAddress: string;
    ImAddressUrl: string;
};

type LpcPhoneData = {
    PhoneNumber: string;
    PhoneUrl: string;
    Type: LpcPhoneDataType;
};

type LpcPhoneDataType =
    | 'Undefined'
    | 'Home'
    | 'Mobile'
    | 'Business'
    | 'BusinessFax'
    | 'HomeFax'
    | 'OtherFax'
    | 'Pager'
    | 'Radio'
    | 'Organization'
    | 'Assistant'
    | 'Other';

type WebSite = {
    Type: WebSiteType;
    Address: string;
};

type WebSiteType = 'Undefined' | 'Personal' | 'Company' | 'Blog' | 'Rss' | 'Portfolio' | 'Other';

type LpcPostalAddressData = {
    City: string;
    CountryOrRegion: string;
    PostalCode: string;
    State: string;
    Street: string;
    Type: string;
};

const lpcPersonaCoinName = (owsPersona: PersonaType): string =>
    (owsPersona.GivenName || owsPersona.Surname) && owsPersona.GivenName + ' ' + owsPersona.Surname;

const lpcEmailAddress = (emailAddress: EmailAddressWrapper): LpcEmailData =>
    emailAddress && {
        Address: emailAddress.EmailAddress,
        Kind: 'Undefined',
    };

const lpcExtraEmailAddresses = (
    emailAddresses: EmailAddressWrapper[],
    emailAddressToIgnore: EmailAddressWrapper
): LpcEmailData[] =>
    emailAddresses &&
    emailAddresses
        .filter(email => email !== emailAddressToIgnore?.EmailAddress)
        .map(email => {
            return lpcEmailAddress(email);
        });

const lpcImAddresses = (owsPersona: PersonaType): ImData[] => {
    return (
        owsPersona.ImAddressesArray &&
        owsPersona.ImAddressesArray.map(value => {
            return (
                value.Value && {
                    ImAddress: value.Value,
                    ImAddressUrl: 'sip:' + value.Value,
                }
            );
        })
    );
};

const lpcPhoneNumbers = (owsPersona: PersonaType): LpcPhoneData[] => {
    const numbers: LpcPhoneData[] = [];

    if (owsPersona.MobilePhonesArray) {
        numbers.push(...phoneNumbersFromArray(owsPersona.MobilePhonesArray, 'Mobile'));
    }

    if (owsPersona.CarPhonesArray) {
        numbers.push(...phoneNumbersFromArray(owsPersona.CarPhonesArray, 'Mobile'));
    }

    if (owsPersona.BusinessPhoneNumbersArray) {
        numbers.push(...phoneNumbersFromArray(owsPersona.BusinessPhoneNumbersArray, 'Business'));
    }

    if (owsPersona.OrganizationMainPhonesArray) {
        numbers.push(
            ...phoneNumbersFromArray(owsPersona.OrganizationMainPhonesArray, 'Organization')
        );
    }

    if (owsPersona.AssistantPhoneNumbersArray) {
        numbers.push(...phoneNumbersFromArray(owsPersona.AssistantPhoneNumbersArray, 'Assistant'));
    }

    if (owsPersona.HomePhonesArray) {
        numbers.push(...phoneNumbersFromArray(owsPersona.HomePhonesArray, 'Home'));
    }

    if (owsPersona.HomePhones2Array) {
        numbers.push(...phoneNumbersFromArray(owsPersona.HomePhones2Array, 'Home'));
    }

    if (owsPersona.PagersArray) {
        numbers.push(...phoneNumbersFromArray(owsPersona.PagersArray, 'Pager'));
    }

    if (owsPersona.WorkFaxesArray) {
        numbers.push(...phoneNumbersFromArray(owsPersona.WorkFaxesArray, 'BusinessFax'));
    }

    if (owsPersona.HomeFaxesArray) {
        numbers.push(...phoneNumbersFromArray(owsPersona.HomeFaxesArray, 'HomeFax'));
    }

    if (owsPersona.OtherFaxesArray) {
        numbers.push(...phoneNumbersFromArray(owsPersona.OtherFaxesArray, 'OtherFax'));
    }

    if (owsPersona.OtherTelephonesArray) {
        numbers.push(...phoneNumbersFromArray(owsPersona.OtherTelephonesArray, 'Other'));
    }

    if (owsPersona.CallbackPhonesArray) {
        numbers.push(...phoneNumbersFromArray(owsPersona.CallbackPhonesArray, 'Other'));
    }

    if (owsPersona.RadioPhonesArray) {
        numbers.push(...phoneNumbersFromArray(owsPersona.RadioPhonesArray, 'Radio'));
    }

    if (owsPersona.TelexNumbersArray) {
        numbers.push(...phoneNumbersFromArray(owsPersona.TelexNumbersArray, 'Other'));
    }

    if (owsPersona.TelexNumbersArray) {
        numbers.push(...phoneNumbersFromArray(owsPersona.TelexNumbersArray, 'Other'));
    }

    if (owsPersona.TTYTDDPhoneNumbersArray) {
        numbers.push(...phoneNumbersFromArray(owsPersona.TTYTDDPhoneNumbersArray, 'Other'));
    }

    return numbers;
};

const phoneNumbersFromArray = (
    values: PhoneNumberAttributedValue[],
    type: LpcPhoneDataType
): LpcPhoneData[] =>
    values.map(value => {
        return (
            value.Value && {
                PhoneNumber: value.Value.Number,
                PhoneUrl: 'tel:' + value.Value.Number,
                Type: type,
            }
        );
    });

const lpcPostalAddresses = (owsPersona: PersonaType): LpcPostalAddressData[] => {
    const addresses: LpcPostalAddressData[] = [];

    if (owsPersona.BusinessAddressesArray) {
        addresses.push(...postalAddressesFromArray(owsPersona.BusinessAddressesArray, 'Business'));
    }

    if (owsPersona.HomeAddressesArray) {
        addresses.push(...postalAddressesFromArray(owsPersona.HomeAddressesArray, 'Home'));
    }

    if (owsPersona.OtherAddressesArray) {
        addresses.push(...postalAddressesFromArray(owsPersona.OtherAddressesArray, 'Other'));
    }

    return addresses;
};

const postalAddressesFromArray = (
    values: PostalAddressAttributedValue[],
    type: string
): LpcPostalAddressData[] =>
    values.map(value => {
        return (
            value.Value && {
                City: value.Value.City,
                CountryOrRegion: value.Value.Country,
                PostalCode: value.Value.PostalCode,
                State: value.Value.State,
                Street: value.Value.Street,
                Type: type,
            }
        );
    });

const lpcBirthDay = (owsPersona: PersonaType): string => owsPersona.BirthdaysArray?.[0]?.Value;

const lpcAnniversary = (owsPersona: PersonaType): string =>
    owsPersona.WeddingAnniversariesArray &&
    owsPersona.WeddingAnniversariesLocalArray[0] &&
    owsPersona.WeddingAnniversariesLocalArray[0].Value;

const lpcWebsites = (owsPersona: PersonaType): WebSite[] => {
    const sites: WebSite[] = [];

    if (owsPersona.BusinessHomePagesArray) {
        sites.push(...websitesFromArray(owsPersona.BusinessHomePagesArray, 'Company'));
    }

    if (owsPersona.PersonalHomePagesArray) {
        sites.push(...websitesFromArray(owsPersona.PersonalHomePagesArray, 'Personal'));
    }

    return sites;
};

const websitesFromArray = (values: StringAttributedValue[], type: WebSiteType): WebSite[] =>
    values.map(value => {
        return (
            value.Value && {
                Address: value.Value,
                Type: type,
            }
        );
    });

const lpcYomiName = (owsPersona: PersonaType): string =>
    (owsPersona.YomiFirstName || owsPersona.YomiLastName) &&
    owsPersona.YomiFirstName + ' ' + owsPersona.YomiLastName;

const lpcManagerFromContacts = (owsPersona: PersonaType): string =>
    owsPersona.ManagersArray?.[0]?.Value;

const lpcAssistantName = (owsPersona: PersonaType): string =>
    owsPersona.AssistantNamesArray?.[0]?.Value;

const lpcSignificantOtherName = (owsPersona: PersonaType): string =>
    owsPersona.SpouseNamesArray?.[0]?.Value;
